import React, { useContext, useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { Card, Col, Row, Spinner, Modal, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ThemeContext } from '../contexts/ThemeContext';
import {
  formatAccountChartValue,
  getAccountColumnDefinitions,
  getAccountColumnLabel,
  getAccountNumericValue
} from '../utils/accountTableConfig';
import { isMarketOpen } from '../utils/dateUtils';
import {
  loadChartDataFromCache,
  saveChartDataToCache
} from '../utils/chartDataCache';
import { saveSelectedMarketIndexes, getSelectedMarketIndexes, validateTicker } from '../services/api';
import tickerPollingService from '../services/tickerPollingService';

Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  Filler,
  ChartDataLabels
);

// Increase the visible bar width while keeping the overall chart height steady.
const BAR_THICKNESS = 26;
const MAX_BAR_THICKNESS = 34;
const MIN_BAR_CHART_HEIGHT = 340;
const BAR_GAP = 14;
const BAR_CHART_ITEM_HEIGHT = BAR_THICKNESS + BAR_GAP;
const TODAY_GAIN_BAR_MIN_HEIGHT = 240;
const INTRADAY_LINE_CHART_HEIGHT = 242; // Increased by 10% from 220 for better readability

const GAIN_COLOR = '#16a34a';
const LOSS_COLOR = '#dc2626';

// Available market indexes (static list, defined once at module scope)
const AVAILABLE_INDEXES = [
  { symbol: 'SPY', name: 'S&P 500' },
  { symbol: 'DIA', name: 'Dow Jones' },
  { symbol: 'QQQ', name: 'NASDAQ-100' },
  { symbol: 'IWM', name: 'Russell 2000' },
  { symbol: 'RSP', name: 'S&P 500 Equal Weight' }
];
const NEUTRAL_COLOR = '#94a3b8';
const resolveValueColor = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value) || value === 0) {
    return NEUTRAL_COLOR;
  }
  return value > 0 ? GAIN_COLOR : LOSS_COLOR;
};

const buildChartDatasets = (
  accounts,
  columnKey,
  accountEdgeMap,
  { includeNegativeSlices = true } = {}
) => {
  if (!accounts || accounts.length === 0 || !columnKey) {
    return {
      sortedItems: [],
      pieValues: [],
      pieLabels: [],
      barValues: [],
      barLabels: [],
      pieRawValues: [],
      totals: includeNegativeSlices ? { gains: 0, losses: 0, net: 0 } : null
    };
  }

  const itemsWithValues = accounts
    .map(account => {
      const value = getAccountNumericValue(account, columnKey, { accountEdgeMap });
      return value === null ? null : { account, value };
    })
    .filter(Boolean);

  const sortedItems = itemsWithValues.sort((a, b) => b.value - a.value);

  const barLabels = sortedItems.map(({ account }) => account.name || '—');
  const barValues = sortedItems.map(item => item.value);
  const pieRawValues = [...barValues];
  const pieValues = includeNegativeSlices
    ? sortedItems.map(item => Math.abs(item.value))
    : (() => {
        const positivePieValues = sortedItems.map(item => Math.max(item.value, 0));
        const positiveTotal = positivePieValues.reduce((sum, value) => sum + value, 0);
        return positiveTotal > 0
          ? positivePieValues
          : sortedItems.map(item => Math.abs(item.value));
      })();
  const pieLabels = barLabels;

  const totals = includeNegativeSlices
    ? (() => {
        const aggregate = pieRawValues.reduce((accumulator, value) => {
          if (value > 0) {
            accumulator.gains += value;
          } else if (value < 0) {
            accumulator.losses += value;
          }
          return accumulator;
        }, { gains: 0, losses: 0 });

        return {
          ...aggregate,
          net: aggregate.gains + aggregate.losses
        };
      })()
    : null;

  return { sortedItems, pieValues, pieLabels, barValues, barLabels, pieRawValues, totals };
};

const AccountsChartView = ({
  accounts,
  selectedColumn,
  accountEdgeMap,
  isLoading,
  todayGainIntradaySeries = [],
  statusTickers = [],
  intradayPeriod = 1,
  onIntradayPeriodChange = () => {},
  onTickerSelectionChange = () => {}
}) => {
  const { effectiveTheme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const intradayGradientRef = useRef(null);
  const [isDuringMarketHours, setIsDuringMarketHours] = useState(() => isMarketOpen());
  const [showIndexSelector, setShowIndexSelector] = useState(false);
  const [customTickerInput, setCustomTickerInput] = useState('');
  const [customTickerError, setCustomTickerError] = useState('');
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const indexSelectorRef = useRef(null);
  const TODAY_GAIN_AXIS_KEY = 'accounts_today_gain_fixed_axis';
  const TODAY_GAIN_CUSTOM_AXIS_KEY = 'accounts_today_gain_fixed_axis_amount';
  const [useFixedTodayGainAxis, setUseFixedTodayGainAxis] = useState(() => {
    try {
      const stored = localStorage.getItem(TODAY_GAIN_AXIS_KEY);
      return stored === null ? false : stored === 'true';
    } catch (err) {
      console.error('Failed to read Today Gain axis preference', err);
      return false;
    }
  });

  // Persist Today Gain axis preference
  useEffect(() => {
    try {
      localStorage.setItem(TODAY_GAIN_AXIS_KEY, useFixedTodayGainAxis ? 'true' : 'false');
    } catch (err) {
      console.error('Failed to persist Today Gain axis preference', err);
    }
  }, [useFixedTodayGainAxis]);

  const [customTodayGainAxisAmount, setCustomTodayGainAxisAmount] = useState(() => {
    try {
      const stored = parseFloat(localStorage.getItem(TODAY_GAIN_CUSTOM_AXIS_KEY));
      return Number.isFinite(stored) && stored > 0 ? stored : 10000;
    } catch (err) {
      console.error('Failed to read Today Gain custom axis amount', err);
      return 10000;
    }
  });
  const [customAxisError, setCustomAxisError] = useState('');
  const [showAxisModal, setShowAxisModal] = useState(false);
  const [pendingAxisValue, setPendingAxisValue] = useState('');
  const presetAxisOptions = [5000, 10000, 15000, 20000];
  const [showIntradayPeriodModal, setShowIntradayPeriodModal] = useState(false);

  // Persist custom axis amount
  useEffect(() => {
    try {
      localStorage.setItem(TODAY_GAIN_CUSTOM_AXIS_KEY, String(customTodayGainAxisAmount));
    } catch (err) {
      console.error('Failed to persist Today Gain custom axis amount', err);
    }
  }, [customTodayGainAxisAmount]);

  // Load selected market indexes on mount
  useEffect(() => {
    const loadSelectedIndexes = async () => {
      try {
        const response = await getSelectedMarketIndexes();
        const selection = response?.data?.selected_indexes || [];
        setSelectedSymbols(selection.length > 0 ? selection : ['SPY', 'QQQ', 'IWM']);
      } catch (error) {
        console.error('Error loading selected market indexes:', error);
        setSelectedSymbols(['SPY', 'QQQ', 'IWM']);
      }
    };
    loadSelectedIndexes();

    // Listen for ticker selection changes from Navigation (status bar)
    const handleTickerSelectionChanged = (event) => {
      const newSelection = event.detail?.selection;
      if (newSelection && Array.isArray(newSelection)) {
        setSelectedSymbols(newSelection);
      } else {
        // If no selection provided, reload from backend
        loadSelectedIndexes();
      }
    };

    window.addEventListener('tickerSelectionChanged', handleTickerSelectionChanged);
    return () => {
      window.removeEventListener('tickerSelectionChanged', handleTickerSelectionChanged);
    };
  }, []);

  // Track selected column to detect changes
  const prevSelectedColumnRef = useRef(selectedColumn);

  // Chart data cache state - load synchronously on mount for instant display
  const [cachedChartData, setCachedChartData] = useState(() => {
    if (!selectedColumn) return null;
    return loadChartDataFromCache('accountsChart', selectedColumn);
  });

  // Track if we're on initial load (no fresh data yet)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Load cached data when selectedColumn changes
  useEffect(() => {
    // Only reload cache if column actually changed
    if (selectedColumn && selectedColumn !== prevSelectedColumnRef.current) {
      prevSelectedColumnRef.current = selectedColumn;
      const cached = loadChartDataFromCache('accountsChart', selectedColumn);
      setCachedChartData(cached);
      setIsInitialLoad(true); // Reset for new column
    }
  }, [selectedColumn]);
  useEffect(() => {
    const updateMarketHours = () => {
      const currentlyOpen = isMarketOpen();
      setIsDuringMarketHours(prev => {
        // Detect transition from closed to open
        if (!prev && currentlyOpen) {
          console.log('[AccountsChartView] Market just opened - resuming polling');
          // Tell the polling service market has opened
          tickerPollingService.setMarketClosed(false);
          // Force an immediate poll to get fresh data
          tickerPollingService.forcePoll();
        }
        return currentlyOpen;
      });
    };
    updateMarketHours();
    const intervalId = setInterval(updateMarketHours, 60 * 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);
  const filteredAccounts = useMemo(
    () => (accounts || []).filter(account => (account.balance || 0) !== 0),
    [accounts]
  );
  const formatIntradayWholeNumber = useCallback((value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return '0';
    }
    return Math.round(value).toLocaleString();
  }, []);
  const getIntradayFillGradient = useCallback((chart) => {
    const { ctx, chartArea } = chart || {};
    if (!ctx || !chartArea) {
      return 'rgba(25, 135, 84, 0.35)';
    }

    const yScale = chart.scales?.y;
    if (!yScale) {
      return 'rgba(25, 135, 84, 0.35)';
    }

    const zeroPixel = yScale.getPixelForValue(0);
    const width = chartArea.right - chartArea.left;
    const height = chartArea.bottom - chartArea.top;
    const cache = intradayGradientRef.current;

    if (
      cache &&
      cache.width === width &&
      cache.height === height &&
      cache.zeroPixel === zeroPixel &&
      cache.top === chartArea.top &&
      cache.bottom === chartArea.bottom
    ) {
      return cache.gradient;
    }

    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    const stopPositionRaw = (zeroPixel - chartArea.top) / height;
    const stopPosition = Math.min(Math.max(stopPositionRaw, 0), 1);

    gradient.addColorStop(0, 'rgba(25, 135, 84, 0.35)');
    gradient.addColorStop(stopPosition, 'rgba(25, 135, 84, 0.35)');
    gradient.addColorStop(stopPosition, 'rgba(220, 53, 69, 0.35)');
    gradient.addColorStop(1, 'rgba(220, 53, 69, 0.35)');

    intradayGradientRef.current = {
      gradient,
      width,
      height,
      zeroPixel,
      top: chartArea.top,
      bottom: chartArea.bottom
    };

    return gradient;
  }, []);

  const { columnLabel, sortedItems, barData, pieData, totals } = useMemo(() => {
    // If we have cached data and haven't computed fresh data yet, use cache immediately
    // This provides instant display while fresh data is being loaded/computed
    if (cachedChartData && isInitialLoad && (!filteredAccounts || filteredAccounts.length === 0)) {
      return {
        columnLabel: cachedChartData.columnLabel || '',
        sortedItems: cachedChartData.sortedItems || [],
        barData: cachedChartData.barData || { labels: [], datasets: [] },
        pieData: cachedChartData.pieData || { labels: [], datasets: [] },
        totals: cachedChartData.totals || null
      };
    }

    const label = getAccountColumnLabel(selectedColumn, 'account');
    const columnDefinitions = getAccountColumnDefinitions('account');
    const columnDefinition = columnDefinitions[selectedColumn] || {};
    const valueType = columnDefinition.valueType;
    const isPercentageColumn =
      valueType === 'percentage' ||
      (typeof selectedColumn === 'string' && /_percentage$/.test(selectedColumn));
    const shouldIncludeNegativeSlices = !isPercentageColumn;
    const {
      sortedItems,
      pieValues,
      pieLabels,
      barValues,
      barLabels,
      pieRawValues,
      totals
    } = buildChartDatasets(
      filteredAccounts,
      selectedColumn,
      accountEdgeMap,
      { includeNegativeSlices: shouldIncludeNegativeSlices }
    );

    const barColors = barValues.map((value) => resolveValueColor(value));

    const barDataset = {
      labels: barLabels,
      datasets: [
        {
          label,
          data: barValues,
          rawData: barValues,
          backgroundColor: barColors,
          borderRadius: 6,
          barThickness: BAR_THICKNESS,
          maxBarThickness: MAX_BAR_THICKNESS,
          datalabels: {
            display: false
          },
          formatValue: (value) => formatAccountChartValue(selectedColumn, value)
        }
      ]
    };

    const pieColors = pieRawValues.map((value) => resolveValueColor(value));

    const pieDataset = {
      labels: pieLabels,
      datasets: [
        {
          label,
          data: pieValues,
          backgroundColor: pieColors,
          borderColor: effectiveTheme === 'dark' ? '#111827' : '#ffffff',
          borderWidth: 2,
          rawData: pieRawValues,
          datalabels: {
            display: false
          },
          formatValue: (value) => formatAccountChartValue(selectedColumn, value)
        }
      ]
    };

    const result = {
      columnLabel: label,
      sortedItems,
      barData: barDataset,
      pieData: pieDataset,
      totals
    };

    // Save to cache when we have valid data and update state for re-render
    // Note: cachedChartData is intentionally excluded from dependencies to prevent infinite loop
    if (sortedItems && sortedItems.length > 0 && selectedColumn) {
      // Use setTimeout to avoid blocking the render
      setTimeout(() => {
        saveChartDataToCache('accountsChart', selectedColumn, result);
        // Update state to trigger re-render with fresh data if user is still on page
        setCachedChartData(result);
        // Mark that we've loaded fresh data
        setIsInitialLoad(false);
      }, 0);
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAccounts, selectedColumn, accountEdgeMap, effectiveTheme, isInitialLoad, accounts]);


  const MAX_SELECTED_SYMBOLS = 6;

  // Handler to click on ticker - toggle selector
  const handleIndexClick = useCallback((e) => {
    e.preventDefault();
    setShowIndexSelector(!showIndexSelector);
  }, [showIndexSelector]);

  // Handler for ticker selection
  const handleTickerSelection = useCallback(async (symbol, isChecked) => {
    let newSelection;

    if (isChecked && selectedSymbols.length < MAX_SELECTED_SYMBOLS && !selectedSymbols.includes(symbol)) {
      newSelection = [...selectedSymbols, symbol].slice(-MAX_SELECTED_SYMBOLS);
    } else if (!isChecked && selectedSymbols.includes(symbol)) {
      newSelection = selectedSymbols.filter(s => s !== symbol);
      if (newSelection.length === 0) {
        return; // Keep at least one
      }
    } else {
      return;
    }

    try {
      await saveSelectedMarketIndexes(newSelection);
      setSelectedSymbols(newSelection);
      // Trigger refresh in parent to sync status bar
      onTickerSelectionChange();
      // Dispatch custom event to notify Navigation component
      window.dispatchEvent(new CustomEvent('tickerSelectionChanged', {
        detail: { selection: newSelection }
      }));
    } catch (error) {
      console.error('Error saving market index selection:', error);
    }
  }, [selectedSymbols, onTickerSelectionChange]);

  // Handler to add custom ticker
  const handleAddCustomTicker = useCallback(async () => {
    const symbol = customTickerInput.trim().toUpperCase();
    if (!symbol) return;

    if (selectedSymbols.includes(symbol)) {
      setCustomTickerError(`${symbol} is already in the list`);
      return;
    }

    if (selectedSymbols.length >= MAX_SELECTED_SYMBOLS) {
      setCustomTickerError(`Maximum ${MAX_SELECTED_SYMBOLS} tickers allowed`);
      return;
    }

    try {
      const validation = await validateTicker(symbol);
      if (!validation.data.valid) {
        setCustomTickerError(`${symbol} is not a valid ticker symbol`);
        return;
      }

      const newSelection = [...selectedSymbols, symbol];
      await saveSelectedMarketIndexes(newSelection);
      setSelectedSymbols(newSelection);
      setCustomTickerInput('');
      setCustomTickerError('');
      // Trigger refresh in parent to sync status bar
      onTickerSelectionChange();
      // Dispatch custom event to notify Navigation component
      window.dispatchEvent(new CustomEvent('tickerSelectionChanged', {
        detail: { selection: newSelection }
      }));
    } catch (error) {
      console.error('Error adding custom ticker:', error);
      setCustomTickerError('Failed to add ticker');
    }
  }, [customTickerInput, selectedSymbols, onTickerSelectionChange]);

  // Handler to remove custom ticker
  const handleRemoveCustomTicker = useCallback(async (symbol) => {
    const newSelection = selectedSymbols.filter(s => s !== symbol);
    if (newSelection.length === 0) return;

    try {
      await saveSelectedMarketIndexes(newSelection);
      setSelectedSymbols(newSelection);
      // Trigger refresh in parent to sync status bar
      onTickerSelectionChange();
      // Dispatch custom event to notify Navigation component
      window.dispatchEvent(new CustomEvent('tickerSelectionChanged', {
        detail: { selection: newSelection }
      }));
    } catch (error) {
      console.error('Error removing custom ticker:', error);
    }
  }, [selectedSymbols, onTickerSelectionChange]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, symbol) => {
    setDraggedItem(symbol);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(async (e, targetSymbol) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetSymbol) return;

    const draggedIndex = selectedSymbols.indexOf(draggedItem);
    const targetIndex = selectedSymbols.indexOf(targetSymbol);

    const newSelection = [...selectedSymbols];
    newSelection.splice(draggedIndex, 1);
    newSelection.splice(targetIndex, 0, draggedItem);

    try {
      await saveSelectedMarketIndexes(newSelection);
      setSelectedSymbols(newSelection);
      // Trigger refresh in parent to sync status bar
      onTickerSelectionChange();
      // Dispatch custom event to notify Navigation component
      window.dispatchEvent(new CustomEvent('tickerSelectionChanged', {
        detail: { selection: newSelection }
      }));
    } catch (error) {
      console.error('Error reordering tickers:', error);
    }
    setDraggedItem(null);
  }, [draggedItem, selectedSymbols, onTickerSelectionChange]);

  // Close selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (indexSelectorRef.current && !indexSelectorRef.current.contains(event.target)) {
        setShowIndexSelector(false);
      }
    };

    if (showIndexSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showIndexSelector]);

  const textColor = effectiveTheme === 'dark' ? '#F9FAFB' : '#1F2937';
  const gridColor = effectiveTheme === 'dark' ? 'rgba(148, 163, 184, 0.3)' : 'rgba(148, 163, 184, 0.4)';
  const isTodayGainColumn = selectedColumn === 'today_gain_amount';
  const intradaySeries = useMemo(() => {
    if (!Array.isArray(todayGainIntradaySeries)) {
      return [];
    }

    const normalized = todayGainIntradaySeries
      .map(entry => {
        const timestampValue = entry?.timestamp;
        const amountValue = typeof entry?.amount === 'number'
          ? entry.amount
          : Number(entry?.amount);
        if (!timestampValue || Number.isNaN(amountValue)) {
          return null;
        }
        const timestamp = new Date(timestampValue);
        if (Number.isNaN(timestamp.getTime())) {
          return null;
        }

        return {
          timestamp,
          label: timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          amount: Math.round(amountValue)
        };
      })
      .filter(Boolean);
    const filtered = normalized.filter(entry => isMarketOpen(entry.timestamp));
    return filtered.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [todayGainIntradaySeries]);
  const chartIntradaySeries = useMemo(() => {
    // Keep all data points if we have 1 or fewer, otherwise skip the first
    if (intradaySeries.length === 0) {
      return intradaySeries;
    }
    // Show single point or remove first point if we have multiple
    return intradaySeries.length === 1 ? intradaySeries : intradaySeries.slice(1);
  }, [intradaySeries]);
  const hasIntradayHistory = intradaySeries.length > 0;
  const hasIntradayLineData = chartIntradaySeries.length > 0;
  // Show chart all day if we have data, not just during market hours
  const shouldShowIntradayChart = isTodayGainColumn && hasIntradayHistory;
  const formatStatusTickerPercentage = useCallback((value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return 'N/A';
    }
    if (Math.abs(value) < 0.01) {
      return '0.00%';
    }
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  }, []);

  const getStatusTickerColorClass = useCallback((value) => {
    if (typeof value !== 'number' || Number.isNaN(value) || Math.abs(value) < 0.01) {
      return 'text-muted';
    }
    return value >= 0 ? 'text-success' : 'text-danger';
  }, []);

  const statusTickerElements = useMemo(() => {
    if (!Array.isArray(statusTickers) || statusTickers.length === 0) {
      return <span className="text-muted small">Status bar tickers unavailable</span>;
    }

    return selectedSymbols.map((symbol, index) => {
      const ticker = statusTickers.find(t => t?.symbol === symbol) || { symbol };
      const hasData = ticker.current_price !== null && ticker.current_price !== undefined;
      const pct = typeof ticker?.percent_change === 'number' ? ticker.percent_change : null;
      const indexInfo = AVAILABLE_INDEXES.find(ai => ai.symbol === symbol);

      return (
        <span key={`${symbol}-${index}`} className="d-inline-flex align-items-center">
          <span
            className="fw-bold me-1"
            style={{ cursor: 'pointer' }}
            onClick={handleIndexClick}
            title={`Click to configure ${indexInfo?.name || symbol}`}
          >
            {symbol}
          </span>
          <span className={hasData && pct !== null ? getStatusTickerColorClass(pct) : 'text-muted'}>
            {hasData && pct !== null ? formatStatusTickerPercentage(pct) : 'N/A'}
          </span>
          {index < selectedSymbols.length - 1 && (
            <span className="text-muted mx-1">|</span>
          )}
        </span>
      );
    });
  }, [formatStatusTickerPercentage, getStatusTickerColorClass, statusTickers, selectedSymbols, handleIndexClick]);

  const intradayLabelIndices = useMemo(() => {
    if (!hasIntradayLineData) {
      return new Set();
    }

    // First, find all local peaks and valleys
    const peaks = [];
    const valleys = [];

    for (let index = 1; index < chartIntradaySeries.length - 1; index += 1) {
      const prev = chartIntradaySeries[index - 1].amount;
      const current = chartIntradaySeries[index].amount;
      const next = chartIntradaySeries[index + 1].amount;

      // Local maxima (peaks for gains)
      if (current >= prev && current >= next && (current > prev || current > next)) {
        peaks.push({ index, value: current });
      }

      // Local minima (valleys/troughs for losses below 0)
      if (current <= prev && current <= next && (current < prev || current < next) && current < 0) {
        valleys.push({ index, value: current });
      }
    }

    // Filter out overlapping labels - keep most significant peak/valley in nearby regions
    const MIN_DISTANCE = 3; // Minimum points between labels

    // Combine all peaks and valleys, then filter together
    const allExtremes = [...peaks, ...valleys];

    if (allExtremes.length === 0) {
      return new Set();
    }

    // Sort by absolute value (most significant first)
    const sorted = allExtremes.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    const kept = [];

    for (const point of sorted) {
      // Check if this point is too close to any already kept point
      const tooClose = kept.some(keptPoint =>
        Math.abs(point.index - keptPoint.index) < MIN_DISTANCE
      );

      if (!tooClose) {
        kept.push(point);
      }
    }

    const labelSet = new Set();
    kept.forEach(p => labelSet.add(p.index));

    return labelSet;
  }, [chartIntradaySeries, hasIntradayLineData]);
  const lastIntradayTimestamp = hasIntradayHistory
    ? intradaySeries[intradaySeries.length - 1].timestamp
    : null;
  const lastUpdatedLabel = lastIntradayTimestamp
    ? lastIntradayTimestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  // Track if the last update is stale (more than 2 minutes old)
  const [isUpdateStale, setIsUpdateStale] = useState(false);

  // Check staleness on mount and when timestamp changes, and periodically
  useEffect(() => {
    const checkStaleness = () => {
      if (!lastIntradayTimestamp || !isDuringMarketHours) {
        setIsUpdateStale(false);
        return;
      }

      const timeSinceUpdate = Date.now() - lastIntradayTimestamp.getTime();
      const twoMinutesInMs = 2 * 60 * 1000;
      setIsUpdateStale(timeSinceUpdate > twoMinutesInMs);
    };

    // Check immediately
    checkStaleness();

    // Check every 10 seconds to keep indicator current
    const intervalId = setInterval(checkStaleness, 10 * 1000);

    return () => clearInterval(intervalId);
  }, [lastIntradayTimestamp, isDuringMarketHours]);

  const intradayLineChartData = useMemo(() => {
    if (!shouldShowIntradayChart || !hasIntradayLineData) {
      return null;
    }

    const borderColor = '#0EA5E9';

    // Create labels with date information for multi-day view
    const labels = chartIntradaySeries.map(entry => {
      const timestamp = entry.timestamp;
      if (intradayPeriod > 1 && timestamp) {
        // For multi-day view, show date + time
        const dateStr = timestamp.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const timeStr = entry.label;
        return `${dateStr} ${timeStr}`;
      }
      return entry.label;
    });

    return {
      labels,
      datasets: [
        {
          label: intradayPeriod > 1 ? `${intradayPeriod}-Day Gain` : "Today's Gain",
          data: chartIntradaySeries.map(entry => entry.amount),
          borderColor,
          backgroundColor: (context) => getIntradayFillGradient(context.chart),
          fill: 'origin',
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 0,
          pointBorderColor: borderColor,
          pointBackgroundColor: effectiveTheme === 'dark' ? '#0f172a' : '#ffffff',
          labelIndices: intradayLabelIndices,
          datalabels: {
            align: (context) => {
              const value = context.dataset.data[context.dataIndex];
              return value < 0 ? 'bottom' : 'top';
            },
            anchor: 'end',
            offset: (context) => {
              const value = context.dataset.data[context.dataIndex];
              return value < 0 ? 3 : -3;
            },
            clamp: true,
            backgroundColor: 'transparent',
            color: (context) => {
              const value = context.dataset.data[context.dataIndex];
              if (value < 0) {
                // Red for losses/valleys - theme-aware
                return effectiveTheme === 'dark' ? '#ff3b30' : '#dc2626';
              } else {
                // Brighter green for gains/peaks - theme-aware
                return effectiveTheme === 'dark' ? '#5efc82' : '#059669';
              }
            },
            font: {
              weight: '600',
              size: 11
            },
            formatter: (value) => formatIntradayWholeNumber(value),
            display: (context) => context?.dataset?.labelIndices?.has(context.dataIndex) || false
          }
        }
      ]
    };
  }, [chartIntradaySeries, effectiveTheme, formatIntradayWholeNumber, getIntradayFillGradient, hasIntradayLineData, intradayLabelIndices, shouldShowIntradayChart, intradayPeriod]);

  const intradayLineChartOptions = useMemo(() => {
    if (!shouldShowIntradayChart) {
      return null;
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      layout: {
        padding: {
          top: 20,
          bottom: 0,
          left: 0,
          right: 0
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false
        }
      },
      scales: {
        x: {
          ticks: {
            color: textColor,
            maxRotation: intradayPeriod > 1 ? 45 : 0,
            minRotation: intradayPeriod > 1 ? 45 : 0,
            autoSkip: true,
            maxTicksLimit: intradayPeriod > 1 ? 20 : undefined,
            padding: 12
          },
          grid: {
            color: gridColor
          }
        },
        y: {
          ticks: {
            color: textColor,
            callback: (value) => formatIntradayWholeNumber(
              typeof value === 'number' ? value : Number(value)
            )
          },
          grid: {
            color: gridColor
          },
          border: {
            display: false
          },
          title: {
            display: false
          }
        }
      }
    };
  }, [formatIntradayWholeNumber, gridColor, shouldShowIntradayChart, textColor, intradayPeriod]);

  const todayGainAxisLimits = useMemo(() => {
    if (!isTodayGainColumn || !useFixedTodayGainAxis) {
      return null;
    }

    const dataset = barData?.datasets?.[0];
    const values = Array.isArray(dataset?.rawData)
      ? dataset.rawData
      : Array.isArray(dataset?.data)
        ? dataset.data
        : [];
    const maxAbs = values.reduce((max, value) => {
      const numeric = typeof value === 'number' ? Math.abs(value) : 0;
      return numeric > max ? numeric : max;
    }, 0);

    // Dynamic base range: keep at least 10% headroom and round to the nearest 1000, minimum 1000.
    const padded = maxAbs * 1.1;
    const baseRange = Math.max(Math.ceil(padded / 1000) * 1000, 1000);

    // Respect user preference but never shrink below data-driven range
    const userRange = Number.isFinite(customTodayGainAxisAmount) && customTodayGainAxisAmount > 0
      ? customTodayGainAxisAmount
      : baseRange;
    const range = Math.max(baseRange, userRange, maxAbs);

    return { min: -range, max: range, range, maxAbs };
  }, [barData, isTodayGainColumn, useFixedTodayGainAxis, customTodayGainAxisAmount]);


  const barOptions = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const account = sortedItems[index]?.account;
        if (account?.account_id) {
          navigate(`/account/${account.account_id}`);
        }
      }
    },
    layout: {
      padding: {
        left: 16,
        right: 64
      }
    },
    plugins: {
      legend: {
        display: false
      },
      datalabels: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: (contextItems) => {
            const firstItem = contextItems?.[0];
            return firstItem?.label ? [firstItem.label] : [];
          },
          label: (context) => {
            const dataset = context.chart.data.datasets?.[context.datasetIndex];
            const rawValue = dataset?.rawData?.[context.dataIndex];
            const value = typeof rawValue === 'number' ? rawValue : context.raw;
            return formatAccountChartValue(selectedColumn, value);
          }
        }
      }
    },
    scales: {
      x: {
        min: todayGainAxisLimits?.min,
        max: todayGainAxisLimits?.max,
        ticks: {
          color: textColor,
          font: {
            size: 12,
            weight: '500'
          }
        },
        grid: { color: gridColor }
      },
      y: {
        ticks: {
          color: textColor,
          font: {
            size: 14,
            weight: '600'
          },
          crossAlign: 'far'
        },
        grid: { display: false }
      }
    }
  }), [gridColor, selectedColumn, textColor, sortedItems, navigate, todayGainAxisLimits]);

  const pieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const account = sortedItems[index]?.account;
        if (account?.account_id) {
          navigate(`/account/${account.account_id}`);
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      datalabels: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: (contextItems) => {
            const firstItem = contextItems?.[0];
            return firstItem?.label ? [firstItem.label] : [];
          },
          label: (context) => {
            const dataset = context.chart.data.datasets?.[context.datasetIndex];
            const rawValue = dataset?.rawData?.[context.dataIndex];
            const value = typeof rawValue === 'number' ? rawValue : context.raw;
            return formatAccountChartValue(selectedColumn, value);
          }
        }
      }
    }
  }), [selectedColumn, sortedItems, navigate]);

  const barValueLabelPlugin = useMemo(() => ({
    id: 'barValueLabelPlugin',
    afterDatasetsDraw(chart) {
      const dataset = chart.data.datasets?.[0];
      if (!dataset) {
        return;
      }

      const meta = chart.getDatasetMeta(0);
      const font = Chart.defaults.font;
      chart.ctx.save();
      chart.ctx.textBaseline = 'middle';
      chart.ctx.font = `600 14px ${font.family}`;

      meta.data.forEach((element, index) => {
        // Use EXACT same logic as tooltip
        const rawValue = dataset.rawData?.[index];
        const value = typeof rawValue === 'number' ? rawValue : chart.data.datasets?.[0]?.data?.[index];
        if (typeof value !== 'number' || Number.isNaN(value)) {
          return;
        }

        // Use the formatValue function stored in the dataset (same as tooltip uses)
        const valueLabel = dataset.formatValue ? dataset.formatValue(value) : value.toLocaleString();
        const { x, y } = element.tooltipPosition();
        const base = element.base ?? 0;
        const offset = 12;
        const maxX = chart.chartArea?.right ?? chart.width;
        const minX = chart.chartArea?.left ?? 0;

        // Position labels to the right of the bar; no left-side flip.
        chart.ctx.textAlign = 'left';
        let labelX = Math.max(x, base) + offset;

        labelX = Math.min(labelX, maxX - 4);
        labelX = Math.max(labelX, minX + 4);
        chart.ctx.fillStyle = resolveValueColor(value);
        chart.ctx.fillText(valueLabel, labelX, y);
      });

      chart.ctx.restore();
    }
  }), []);


  const pieSliceLabelPlugin = useMemo(() => ({
    id: 'pieSliceLabelPlugin',
    afterDatasetsDraw(chart) {
      const dataset = chart.data.datasets?.[0];
      if (!dataset) {
        return;
      }

      const total = dataset.data.reduce((sum, value) => {
        if (typeof value !== 'number' || Number.isNaN(value)) {
          return sum;
        }
        return sum + Math.abs(value);
      }, 0);

      if (total <= 0) {
        return;
      }

      const meta = chart.getDatasetMeta(0);
      const font = Chart.defaults.font;
      chart.ctx.save();
      chart.ctx.fillStyle = textColor;
      chart.ctx.textAlign = 'center';
      chart.ctx.textBaseline = 'middle';
      chart.ctx.font = `600 14px ${font.family}`;

      meta.data.forEach((element, index) => {
        const displayedValue = dataset.data[index];
        if (typeof displayedValue !== 'number' || Number.isNaN(displayedValue)) {
          return;
        }

        const ratio = Math.abs(displayedValue) / total;
        if (ratio < 0.05) {
          return;
        }

        // Use EXACT same logic as tooltip
        const rawValue = dataset.rawData?.[index];
        const value = typeof rawValue === 'number' ? rawValue : displayedValue;
        // Use the formatValue function stored in the dataset (same as tooltip uses)
        const valueLabel = dataset.formatValue ? dataset.formatValue(value) : value.toLocaleString();
        const { x, y } = element.tooltipPosition();
        chart.ctx.fillText(valueLabel, x, y);
      });

      chart.ctx.restore();
    }
  }), [textColor]);

  const hasData = sortedItems.length > 0;
  const minHeightForSelectedColumn = shouldShowIntradayChart
    ? TODAY_GAIN_BAR_MIN_HEIGHT
    : MIN_BAR_CHART_HEIGHT;
  const barChartHeight = Math.max(
    minHeightForSelectedColumn,
    sortedItems.length * BAR_CHART_ITEM_HEIGHT
  );

  const hasTotals = Boolean(totals);
  const totalGains = hasTotals ? totals.gains : 0;
  const totalLosses = hasTotals ? totals.losses : 0;
  const netValue = hasTotals ? totals.net : 0;

  const netClass = netValue > 0 ? 'text-success' : netValue < 0 ? 'text-danger' : 'text-muted';
  const maxTodayGainAbs = todayGainAxisLimits?.maxAbs || 0;

  const openAxisModal = () => {
    setPendingAxisValue(
      customTodayGainAxisAmount && Number.isFinite(customTodayGainAxisAmount)
        ? String(customTodayGainAxisAmount)
        : todayGainAxisLimits?.range
          ? String(todayGainAxisLimits.range)
          : '10000'
    );
    setCustomAxisError('');
    setShowAxisModal(true);
  };

  const handleCustomAxisInputChange = (event) => {
    setPendingAxisValue(event.target.value);
    setCustomAxisError('');
  };

  const handleSaveCustomAxis = () => {
    const numeric = Number(pendingAxisValue);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      setCustomAxisError('Enter a positive number');
      return;
    }
    if (numeric < maxTodayGainAbs) {
      setCustomAxisError(`Value must be >= current max (${formatAccountChartValue(selectedColumn, maxTodayGainAbs)})`);
      return;
    }
    setCustomTodayGainAxisAmount(numeric);
    setCustomAxisError('');
    setShowAxisModal(false);
  };

  return (
    <>
      <Row className="g-3 accounts-charts-row">
        <Col xl={8} lg={7}>
          <Card className="h-100 accounts-card">
            <Card.Header className="fw-bold">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <span>{columnLabel} by Account</span>
                {isTodayGainColumn && (
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <span className="text-muted small">Fixed axis</span>
                    <div className="form-check form-switch m-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="todayGainFixedAxis"
                        checked={useFixedTodayGainAxis}
                        onChange={(e) => setUseFixedTodayGainAxis(e.target.checked)}
                      />
                    </div>
                    {useFixedTodayGainAxis && (
                      <>
                        <span className="badge bg-light text-dark border">
                          ±{formatAccountChartValue(selectedColumn, todayGainAxisLimits?.range || 0)}
                        </span>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={openAxisModal}
                        >
                          Set range
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Card.Header>
            <Card.Body
              className="position-relative"
              style={{
                cursor: hasData ? 'pointer' : 'default'
              }}
            >
              <div
                style={{
                  minHeight: `${barChartHeight}px`,
                  height: `${barChartHeight}px`
                }}
              >
                {(isLoading && isInitialLoad && !cachedChartData) ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <Spinner animation="border" role="status" variant="primary">
                      <span className="visually-hidden">Loading charts...</span>
                    </Spinner>
                  </div>
                ) : hasData ? (
                  <Bar
                    key={`accounts-bar-${selectedColumn}`}
                    data={barData}
                    options={barOptions}
                    plugins={[barValueLabelPlugin]}
                  />
                ) : (
                  <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                    No chart data available.
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={4} lg={5}>
          <Card className="h-100 accounts-card">
            <Card.Header className="fw-bold">
              {columnLabel} Distribution
            </Card.Header>
            <Card.Body className="position-relative" style={{ minHeight: '380px', cursor: hasData ? 'pointer' : 'default' }}>
              {(isLoading && isInitialLoad && !cachedChartData) ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading charts...</span>
                  </Spinner>
                </div>
              ) : hasData ? (
                <div className="d-flex flex-column h-100">
                  <div className="flex-grow-1 position-relative" style={{ minHeight: '240px' }}>
                    <Doughnut
                      key={`accounts-doughnut-${selectedColumn}`}
                      data={pieData}
                      options={pieOptions}
                      plugins={[pieSliceLabelPlugin]}
                    />
                    {/* Net amount in center of doughnut */}
                    {hasTotals && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none',
                        textAlign: 'center',
                        backgroundColor: netValue > 0
                          ? 'rgba(22, 163, 74, 0.15)'
                          : netValue < 0
                            ? 'rgba(220, 38, 38, 0.15)'
                            : 'transparent',
                        borderRadius: '50%',
                        padding: '24px',
                        minWidth: '140px',
                        minHeight: '140px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.3s ease'
                      }}>
                        <span className={netClass} style={{
                          fontWeight: 'bold',
                          fontSize: '1.5rem',
                          whiteSpace: 'nowrap'
                        }}>
                          {formatAccountChartValue(selectedColumn, netValue)}
                        </span>
                      </div>
                    )}
                  </div>
                  {hasTotals && (
                    <div className="mt-3 pt-3">
                      {/* Two separate bars showing gains and losses */}
                      <div className="mb-2">
                        {/* Calculate max value for scaling */}
                        {(() => {
                          const maxValue = Math.max(totalGains, Math.abs(totalLosses));

                          return (
                            <>
                              {/* Gains bar (top) */}
                              <div className="mb-2">
                                <div style={{
                                  height: '20px',
                                  display: 'flex',
                                  borderRadius: '4px',
                                  overflow: 'hidden',
                                  backgroundColor: effectiveTheme === 'dark' ? '#374151' : '#e5e7eb'
                                }}>
                                  <div
                                    style={{
                                      width: maxValue > 0 ? `${(totalGains / maxValue) * 100}%` : '0%',
                                      backgroundColor: totalGains > 0 ? GAIN_COLOR : NEUTRAL_COLOR,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                      paddingRight: '8px',
                                      color: 'white',
                                      fontSize: '0.7rem',
                                      fontWeight: '600',
                                      transition: 'width 0.3s ease',
                                      minWidth: totalGains > 0 ? 'fit-content' : '0'
                                    }}
                                    title={`Gains: ${formatAccountChartValue(selectedColumn, totalGains)}`}
                                  >
                                    {totalGains > 0 && (
                                      <span style={{ whiteSpace: 'nowrap', paddingLeft: '8px' }}>
                                        {formatAccountChartValue(selectedColumn, totalGains)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Losses bar (bottom) */}
                              <div>
                                <div style={{
                                  height: '20px',
                                  display: 'flex',
                                  borderRadius: '4px',
                                  overflow: 'hidden',
                                  backgroundColor: effectiveTheme === 'dark' ? '#374151' : '#e5e7eb'
                                }}>
                                  <div
                                    style={{
                                      width: maxValue > 0 ? `${(Math.abs(totalLosses) / maxValue) * 100}%` : '0%',
                                      backgroundColor: totalLosses < 0 ? LOSS_COLOR : NEUTRAL_COLOR,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                      paddingRight: '8px',
                                      color: 'white',
                                      fontSize: '0.7rem',
                                      fontWeight: '600',
                                      transition: 'width 0.3s ease',
                                      minWidth: totalLosses < 0 ? 'fit-content' : '0'
                                    }}
                                    title={`Losses: ${formatAccountChartValue(selectedColumn, totalLosses)}`}
                                  >
                                    {totalLosses < 0 && (
                                      <span style={{ whiteSpace: 'nowrap', paddingLeft: '8px' }}>
                                        {formatAccountChartValue(selectedColumn, totalLosses)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                  No distribution data available.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {isTodayGainColumn && (
        <Row className="g-3 mt-2">
          <Col>
            <Card className="accounts-card">
              <Card.Header className="fw-bold">
                <div className="d-flex flex-column flex-xl-row gap-2 justify-content-between align-items-xl-center">
                  <div className="d-flex align-items-center gap-3 flex-shrink-0">
                    <span>Intraday Gain Trend</span>
                    {(() => {
                      const totalGainAmount = filteredAccounts.reduce((sum, account) => sum + (account.today_gain_amount || 0), 0);
                      const totalPreviousDayBalance = filteredAccounts.reduce((sum, account) => sum + (account.previous_day_balance || 0), 0);
                      const pct = totalPreviousDayBalance > 0 ? (totalGainAmount / totalPreviousDayBalance) * 100 : 0;
                      const sign = pct >= 0 ? '+' : '';
                      const display = Math.abs(pct) < 0.01 ? '0.00%' : `${sign}${pct.toFixed(2)}%`;
                      const amountDisplay = formatAccountChartValue('today_gain_amount', totalGainAmount);
                      return (
                        <span className={`${getStatusTickerColorClass(pct)} fw-semibold`} title="Total gain percentage across accounts">
                          {amountDisplay} ({display})
                        </span>
                      );
                    })()}
                  </div>
                  <div className="d-flex flex-wrap align-items-center justify-content-xl-center gap-1 text-muted flex-grow-1 position-relative" style={{ fontSize: '0.85rem' }}>
                    {statusTickerElements}

                    {/* Ticker Configuration Selector */}
                    {showIndexSelector && (
                      <div
                        ref={indexSelectorRef}
                        className="position-absolute rounded shadow-lg p-3 mt-1"
                        style={{
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 1000,
                          minWidth: '320px',
                          width: 'max-content',
                          backgroundColor: effectiveTheme === 'dark' ? '#212529' : '#ffffff',
                          border: `1px solid ${effectiveTheme === 'dark' ? '#495057' : '#dee2e6'}`,
                          color: effectiveTheme === 'dark' ? '#f8f9fa' : '#212529'
                        }}
                      >
                        <div className="small fw-bold mb-2 text-center">Select Ticker Symbols (max {MAX_SELECTED_SYMBOLS}):</div>

                        {/* Add Custom Ticker Section */}
                        <div className="mb-3 p-2 rounded" style={{
                          backgroundColor: effectiveTheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                          border: `1px solid ${effectiveTheme === 'dark' ? '#495057' : '#dee2e6'}`
                        }}>
                          <div className="small fw-bold mb-2" style={{ color: effectiveTheme === 'dark' ? '#adb5bd' : '#6c757d' }}>
                            <i className="bi bi-plus-circle me-1"></i>
                            Add Custom Ticker:
                          </div>
                          <div className="d-flex" style={{ gap: '0.5rem' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Enter ticker symbol (e.g., AAPL, TSLA)"
                              value={customTickerInput}
                              onChange={(e) => setCustomTickerInput(e.target.value.toUpperCase())}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddCustomTicker();
                                }
                              }}
                              maxLength={10}
                              style={{
                                backgroundColor: effectiveTheme === 'dark' ? 'rgba(255,255,255,0.1)' : '#ffffff',
                                color: effectiveTheme === 'dark' ? '#ffffff' : '#212529',
                                border: `1px solid ${effectiveTheme === 'dark' ? '#495057' : '#ced4da'}`
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={handleAddCustomTicker}
                              disabled={!customTickerInput.trim() || selectedSymbols.length >= MAX_SELECTED_SYMBOLS}
                              title={selectedSymbols.length >= MAX_SELECTED_SYMBOLS ? `Maximum ${MAX_SELECTED_SYMBOLS} tickers allowed` : "Add ticker"}
                            >
                              <i className="bi bi-plus"></i>
                            </button>
                          </div>
                          {customTickerError && (
                            <div className="small text-danger mt-1">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              {customTickerError}
                            </div>
                          )}
                          {selectedSymbols.length >= MAX_SELECTED_SYMBOLS && !customTickerError && (
                            <div className="small text-warning mt-1">
                              <i className="bi bi-info-circle me-1"></i>
                              Remove a ticker first to add a new one (max {MAX_SELECTED_SYMBOLS} allowed)
                            </div>
                          )}
                        </div>

                        <div className="mb-2">
                          <div className="small mb-2" style={{ color: effectiveTheme === 'dark' ? '#adb5bd' : '#6c757d' }}>
                            <i className="bi bi-arrows-move me-1"></i>
                            Drag to reorder:
                          </div>

                          {/* Show selected tickers in order for reordering */}
                          {selectedSymbols.map((symbol, idx) => {
                            const indexInfo = AVAILABLE_INDEXES.find(ai => ai.symbol === symbol);
                            const isCustomTicker = !indexInfo;
                            return (
                              <div
                                key={symbol}
                                draggable
                                onDragStart={(e) => handleDragStart(e, symbol)}
                                onDragEnd={handleDragEnd}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, symbol)}
                                className={`d-flex justify-content-between align-items-center py-2 px-2 rounded mb-1 ${
                                  draggedItem === symbol ? 'opacity-50' : ''
                                }`}
                                style={{
                                  border: `1px solid ${effectiveTheme === 'dark' ? '#495057' : '#dee2e6'}`,
                                  backgroundColor: effectiveTheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                  cursor: 'grab'
                                }}
                              >
                                <div className="d-flex align-items-center flex-grow-1">
                                  <i
                                    className="bi bi-grip-vertical"
                                    style={{
                                      color: effectiveTheme === 'dark' ? '#adb5bd' : '#6c757d',
                                      marginRight: '8px'
                                    }}
                                  ></i>
                                  <span className="small fw-bold me-1">#{idx + 1}</span>
                                  <span className="small">
                                    <strong>{symbol}</strong>
                                    {isCustomTicker ? (
                                      <span className="text-muted ms-1">(Custom)</span>
                                    ) : (
                                      <span> - {indexInfo.name}</span>
                                    )}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  {isCustomTicker && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleRemoveCustomTicker(symbol)}
                                      title="Remove custom ticker"
                                      style={{
                                        padding: '0.125rem 0.375rem',
                                        fontSize: '0.7rem',
                                        lineHeight: '1'
                                      }}
                                    >
                                      <i className="bi bi-trash3"></i>
                                    </button>
                                  )}
                                  <input
                                    type="checkbox"
                                    checked={true}
                                    onChange={(e) => handleTickerSelection(symbol, e.target.checked)}
                                    style={{ filter: effectiveTheme === 'dark' ? 'invert(1)' : 'none' }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div
                          className="small fw-bold mb-2 pt-2"
                          style={{
                            borderTop: `1px solid ${effectiveTheme === 'dark' ? '#495057' : '#dee2e6'}`
                          }}
                        >
                          Available Indexes:
                        </div>
                        {AVAILABLE_INDEXES
                          .filter(availIndex => !selectedSymbols.includes(availIndex.symbol))
                          .map(availIndex => (
                            <div
                              key={availIndex.symbol}
                              className="d-flex justify-content-between align-items-center py-1 px-2 rounded"
                              style={{ cursor: 'pointer' }}
                            >
                              <span className="small">
                                <strong>{availIndex.symbol}</strong> - {availIndex.name}
                              </span>
                              <input
                                type="checkbox"
                                checked={false}
                                onChange={(e) => handleTickerSelection(availIndex.symbol, e.target.checked)}
                                style={{ filter: effectiveTheme === 'dark' ? 'invert(1)' : 'none' }}
                              />
                            </div>
                          ))}
                        <div className="text-center mt-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setShowIndexSelector(false)}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowIntradayPeriodModal(true)}
                    >
                      {intradayPeriod === 1 ? 'Today' : `${intradayPeriod} Days`}
                    </Button>
                    {shouldShowIntradayChart && lastUpdatedLabel && (
                      <span className="small text-muted d-flex align-items-center gap-2">
                        Last update {lastUpdatedLabel}
                        <span
                          style={{
                            display: 'inline-block',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: isUpdateStale ? '#dc2626' : '#16a34a',
                            boxShadow: isUpdateStale
                              ? '0 0 8px rgba(220, 38, 38, 0.6)'
                              : '0 0 8px rgba(22, 163, 74, 0.6)',
                            transition: 'all 0.3s ease',
                            cursor: 'help'
                          }}
                          title={isUpdateStale ? 'No update for more than 2 minutes' : 'Updates are current'}
                        ></span>
                      </span>
                    )}
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {shouldShowIntradayChart ? (
                  intradayLineChartData ? (
                    <div style={{ minHeight: `${INTRADAY_LINE_CHART_HEIGHT}px` }}>
                      <Line
                        key={`accounts-line-today-gain-${chartIntradaySeries.length}`}
                        data={intradayLineChartData}
                        options={intradayLineChartOptions}
                      />
                    </div>
                  ) : (
                    <div className="text-muted small text-center py-4">
                      Intraday history will appear after the next gain update.
                    </div>
                  )
                ) : (
                  <div className="text-muted small text-center py-4">
                    No intraday data available. Data is collected during market hours (9:30 AM - 4:00 PM ET) on trading days.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      {/* Custom axis modal */}
      <Modal show={showAxisModal} onHide={() => setShowAxisModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Set fixed axis range</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2 text-muted small">
            Enter a positive value equal to or above the current data max to set the ±axis range.
          </p>
          <Form.Group controlId="customAxisInput">
            <Form.Label className="small mb-1">Custom max (applies symmetrically)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="500"
              value={pendingAxisValue}
              onChange={handleCustomAxisInputChange}
              autoFocus
            />
            {todayGainAxisLimits?.maxAbs > 0 && (
              <Form.Text muted>
                Current data max: {formatAccountChartValue(selectedColumn, todayGainAxisLimits.maxAbs)}
              </Form.Text>
            )}
            {customAxisError && (
              <div className="text-danger small mt-1">{customAxisError}</div>
            )}
          </Form.Group>
          <div className="mt-3">
            <div className="small fw-bold mb-2">Presets</div>
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              {presetAxisOptions.map(option => (
                <Button
                  key={`axis-preset-${option}`}
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setPendingAxisValue(String(option));
                    setCustomAxisError('');
                    // Apply immediately and close
                    const numeric = Number(option);
                    setCustomTodayGainAxisAmount(numeric);
                    setShowAxisModal(false);
                  }}
                >
                  {formatAccountChartValue(selectedColumn, option)}
                </Button>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAxisModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveCustomAxis}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Intraday period modal */}
      <Modal show={showIntradayPeriodModal} onHide={() => setShowIntradayPeriodModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select intraday period</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            {[1, 2, 3, 5].map(period => (
              <Button
                key={`intraday-period-${period}`}
                variant={intradayPeriod === period ? 'primary' : 'outline-secondary'}
                onClick={() => {
                  onIntradayPeriodChange(period);
                  setShowIntradayPeriodModal(false);
                }}
              >
                {period === 1 ? 'Today' : `${period} Days`}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowIntradayPeriodModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AccountsChartView;
